// eslint-disable-next-line jira/filename-case
import React, { type MouseEvent } from 'react';
import { ViewTemplatesButton } from '@jira/polaris__component-view-template-library/src/ViewTemplatesButton.tsx';
import type { ViewKind } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
import { AiGenerateViewsMenuButton } from '@jira/polaris__component-ai-view-generation/src/AiGenerateViewsMenuButton.tsx';
import { useCanCreateFields } from '@jira/polaris__component-permissions-store/src/useCanCreateFields.tsx';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { cssMap } from '@atlaskit/css';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';

import { type NavigationContainer, isNavigationProject } from '../../../common/types.tsx';
import { ViewMenuItems } from './ViewMenuItems.tsx';

const stopEventPropagation = (event: MouseEvent<HTMLDivElement>) => event.stopPropagation();

const MIN_WIDTH_IN_PX = 250;

const styles = cssMap({
	menu: {
		minWidth: `${MIN_WIDTH_IN_PX}px`,
	},
});

export const MENU_TEST_ID =
	'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.ui.create-view-menu.menu.create-view-menu';

export type MenuProps = {
	container?: string;
	sectionId?: string;
	navigationContainer: NavigationContainer;
	onMenuItemClick: (arg: { type: ViewKind; container?: string }) => void;
	onPopupClose: () => void;
};

export const Menu = ({
	container,
	sectionId,
	navigationContainer,
	onMenuItemClick,
	onPopupClose,
}: MenuProps): React.JSX.Element => {
	const canCreateFields = useCanCreateFields();

	const isViewTemplateLibraryEnabled =
		canCreateFields &&
		isNavigationProject(navigationContainer) &&
		(expVal('view-template-library-m1-for-new-tenants', 'isEnabled', false) ||
			expVal('view-template-library-m1-for-existing-tenants', 'isEnabled', false));

	return (
		<Box onMouseEnter={stopEventPropagation} onMouseLeave={stopEventPropagation}>
			<MenuGroup minWidth={MIN_WIDTH_IN_PX} testId={MENU_TEST_ID}>
				<Flex>
					<Box xcss={styles.menu}>
						<ViewMenuItems
							container={container}
							navigationContainer={navigationContainer}
							onClick={onMenuItemClick}
						/>
						{isViewTemplateLibraryEnabled && (
							<Section hasSeparator>
								<ViewTemplatesButton
									onClick={onPopupClose}
									projectId={navigationContainer.projectId}
									projectKey={navigationContainer.projectKey}
									containerId={sectionId}
								/>
							</Section>
						)}
						{isNavigationProject(navigationContainer) && fg('jpd_ai_view_generation') && (
							<AiGenerateViewsMenuButton
								onClick={onPopupClose}
								projectId={navigationContainer.projectId}
								projectKey={navigationContainer.projectKey}
								containerId={sectionId}
							/>
						)}
					</Box>
				</Flex>
			</MenuGroup>
		</Box>
	);
};
